'use client';

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { type Locale, type Translations, DEFAULT_LOCALE, LOCALE_COOKIE } from '@/lib/i18n';
import { getTranslations } from '@/lib/i18n/translations';
import { stripLocalePrefix } from '@/lib/i18n/href';
import { analytics, AnalyticsEvents } from '@/lib/analytics';

interface LanguageContextType {
  locale: Locale;
  t: Translations;
  setLocale: (locale: Locale) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

function getInitialLocale(serverLocale?: Locale): Locale {
  if (serverLocale && ['en', 'es', 'pt', 'de', 'fr'].includes(serverLocale)) {
    return serverLocale;
  }
  if (typeof document !== 'undefined') {
    const match = document.cookie.match(new RegExp(`${LOCALE_COOKIE}=([^;]+)`));
    if (match && ['en', 'es', 'pt', 'de', 'fr'].includes(match[1])) {
      return match[1] as Locale;
    }
  }
  return DEFAULT_LOCALE;
}

export function LanguageProvider({ children, serverLocale }: { children: ReactNode; serverLocale?: Locale }) {
  const pathname = usePathname();
  const router = useRouter();
  const [locale, setLocaleState] = useState<Locale>(() => getInitialLocale(serverLocale));

  const setLocale = useCallback((newLocale: Locale) => {
    document.cookie = `${LOCALE_COOKIE}=${newLocale};path=/;max-age=${60 * 60 * 24 * 365}`;
    analytics.capture(AnalyticsEvents.LANGUAGE_CHANGED, { language: newLocale });
    // La elección se GUARDA en el perfil del usuario (users.platform_language):
    // la cookie es por navegador y "siempre se volvía a poner el de antes" al
    // entrar desde otro lado (Andrey, 10 sep 2026). keepalive: la petición
    // sobrevive a la navegación dura de abajo. Best-effort — sin sesión (p.ej.
    // landing) el endpoint responde 401 y no pasa nada.
    try {
      void fetch('/api/users/platform-language', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ locale: newLocale }),
        keepalive: true,
      }).catch(() => {});
    } catch { /* la preferencia local ya quedó en la cookie */ }
    setLocaleState(newLocale);
    const bare = stripLocalePrefix(pathname || '/');
    const target = bare === '/' ? `/${newLocale}` : `/${newLocale}${bare}`;
    router.push(target, { scroll: false });
    router.refresh();
  }, [pathname, router]);

  const t = getTranslations(locale);

  return (
    <LanguageContext.Provider value={{ locale, t, setLocale }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}
